import React, { useState } from 'react';
import { Badge, Card, Col, Modal, Row } from 'react-bootstrap';

const BASE_URL = process.env.REACT_APP_API_URL;

const MediaPreview = ({ mediaFiles = [], baseUrl, onRemove, allowRemove = true }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getFileType = (file) => {
    try {
      // For direct File objects
      if (file instanceof File) {
        return file.type.split('/')[1]?.toLowerCase() || '';
      }

      // For objects with file property (new uploads)
      if (file?.file instanceof File) {
        return file.file.type.split('/')[1]?.toLowerCase() || '';
      }

      // For existing files with filename or name
      const fileName = file?.filename || file?.name || (typeof file === 'string' ? file : '');
      if (fileName) {
        const extension = fileName.split('.').pop()?.toLowerCase();
        return extension || '';
      }

      return '';
    } catch (error) {
      console.error('Error getting file type:', error);
      return '';
    }
  };

  const getFileUrl = (file) => {
    try {
      if (!file) return '';

      // For new File objects or objects containing File
      if (file instanceof File || file?.file instanceof File) {
        const fileObj = file instanceof File ? file : file.file;
        return URL.createObjectURL(fileObj);
      }

      // For existing files from server
      const fileName = file?.filename || file?.name || (typeof file === 'string' ? file : '');
      if (fileName) {
        return `${baseUrl}/${fileName}`;
      }

      return '';
    } catch (error) {
      console.error('Error getting file URL:', error);
      return '';
    }
  };

  const getFileName = (file) => {
    try {
      if (!file) return 'Unknown file';

      // For direct File objects
      if (file instanceof File) return file.name;

      // For objects with file property (new uploads)
      if (file?.file instanceof File) return file.file.name;

      // For existing files
      if (file?.filename || file?.name) return file.filename || file.name;

      // For string paths
      if (typeof file === 'string') {
        const parts = file.split('/');
        return parts[parts.length - 1];
      }

      return 'Unknown file';
    } catch (error) {
      console.error('Error getting file name:', error);
      return 'Unknown file';
    }
  };

  // Add this new function to handle file deletion from storage
  const handleRemove = async (index) => {
    // Don't allow removal if allowRemove is false
    if (!allowRemove) return;
    
    const file = files[index];
    
    // If it's a new file (not yet saved to server), just remove from state
    if (file?.isNew) {
      onRemove(index);
      return;
    }
    
    // Get filename for existing file
    const fileName = file?.filename || file?.name || (typeof file === 'string' ? file : '');
    
    if (!fileName) {
      // If we can't get a filename, just remove from state
      onRemove(index);
      return;
    }
    
    setIsDeleting(true);
    
    try {
      // Call the server to delete the file
      const response = await fetch(`${BASE_URL}/delete-media-file/${fileName}`, {
        method: 'DELETE',
      });
      
      
      // Remove from state regardless of server response
      // This prevents UI inconsistency if file deletion fails on server
      onRemove(index);
    } catch (error) {
      console.error('Error deleting file:', error);
      // Still remove from UI to maintain consistency
      onRemove(index);
    } finally {
      setIsDeleting(false);
    }
  };

  const files = Array.isArray(mediaFiles) ? mediaFiles : [mediaFiles].filter(Boolean);

  const getFileIcon = (fileType) => {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileType)) {
      return 'image';
    }
    if (['mp4', 'webm', 'mov'].includes(fileType)) {
      return 'video';
    }
    if (['mp3', 'wav', 'ogg'].includes(fileType)) {
      return 'volume-up';
    }
    if (['pdf'].includes(fileType)) {
      return 'file-pdf';
    }
    if (['doc', 'docx'].includes(fileType)) {
      return 'file-word';
    }
    if (['xls', 'xlsx'].includes(fileType)) {
      return 'file-excel';
    }
    return 'file';
  };

  const renderThumbnail = (file, fileType, fileUrl) => {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileType)) {
      return (
        <div className="card-img-wrapper">
          <img src={fileUrl} alt="Preview" className="card-img" />
        </div>
      );
    }

    if (['mp4', 'webm', 'mov'].includes(fileType)) {
      return (
        <div className="card-img-wrapper video-wrapper">
          <div className="video-thumbnail">
            <i className="fas fa-play-circle video-icon"></i>
          </div>
        </div>
      );
    }
    
    return (
      <div className="card-img-wrapper file-wrapper">
        <i className={`fas fa-${getFileIcon(fileType)} file-icon`}></i>
      </div>
    );
  };

  const renderPreviewContent = (file) => {
    const fileType = getFileType(file);
    const fileUrl = getFileUrl(file);

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileType)) {
      return <img src={fileUrl} alt="Preview" className="img-fluid" />;
    }

    if (['mp4', 'webm', 'mov'].includes(fileType)) {
      return (
        <video controls className="w-100">
          <source src={fileUrl} type={`video/${fileType}`} />
          Your browser does not support video playback.
        </video>
      );
    }

    if (['mp3', 'wav', 'ogg'].includes(fileType)) {
      return (
        <audio controls className="w-100">
          <source src={fileUrl} type={`audio/${fileType}`} />
          Your browser does not support audio playback.
        </audio>
      );
    }

    return (
      <div className="text-center p-5">
        <i className={`fas fa-${getFileIcon(fileType)} fa-3x mb-3 text-muted`}></i>
        <p>Preview not available</p>
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
          Open File
        </a>
      </div>
    );
  };

  return (
    <>
      <Row xs={2} sm={3} md={4} lg={6} className="g-3">
        {files.map((file, index) => {
          const fileType = getFileType(file);
          const fileUrl = getFileUrl(file);
          const fileName = getFileName(file);
          
          return (
            <Col key={index}>
              <Card className="h-100 media-card">
                <div 
                  className="card-thumbnail"
                  onClick={() => setSelectedFile(file)}
                >
                  {renderThumbnail(file, fileType, fileUrl)}
                  {file.isNew && (
                    <Badge bg="info" className="new-badge">New</Badge>
                  )}
                  {allowRemove && (
                    <div 
                      className="remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(index);
                      }}
                      title="Remove file"
                    >
                      {isDeleting ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        <i className="fas fa-times"></i>
                      )}
                    </div>
                  )}
                </div>
                
                <Card.Footer className="p-2">
                  <div className="file-name" title={fileName}>
                    {fileName.length > 12 ? fileName.substring(0, 12) + '...' : fileName}
                  </div>
                  <div className="d-flex justify-content-center mt-1">
                    <button 
                      className="view-btn"
                      onClick={() => setSelectedFile(file)}
                    >
                      <i className="fas fa-eye me-1"></i> View
                    </button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Modal 
        show={selectedFile !== null} 
        onHide={() => setSelectedFile(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedFile && getFileName(selectedFile)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFile && renderPreviewContent(selectedFile)}
        </Modal.Body>
      </Modal>

      <style jsx>{`
        .media-card {
          transition: all 0.2s;
          cursor: pointer;
          overflow: hidden;
          border: 1px solid rgba(0,0,0,.125);
          border-radius: 6px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.05);
        }
        
        .media-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }
        
        .card-thumbnail {
          position: relative;
          height: 130px;
          background-color: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        
        .card-img-wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .card-img {
          object-fit: cover;
          width: 100%;
          height: 100%;
        }
        
        .video-wrapper {
          background-color: #000;
        }
        
        .video-thumbnail {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .video-icon {
          font-size: 36px;
          color: white;
          opacity: 0.9;
        }
        
        .file-wrapper {
          background-color: #f1f3f5;
        }
        
        .file-icon {
          font-size: 36px;
          color: var(--bs-primary);
        }
        
        .file-name {
          font-size: 0.75rem;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .view-btn {
          background-color: #f1f3f5;
          border: none;
          border-radius: 3px;
          font-size: 0.7rem;
          padding: 2px 8px;
          transition: all 0.2s;
        }
        
        .view-btn:hover {
          background-color: var(--bs-primary);
          color: white;
        }
        
        .new-badge {
          position: absolute;
          top: 5px;
          left: 5px;
          font-size: 0.6rem;
          padding: 0.25rem 0.4rem;
        }
        
        .remove-btn {
          position: absolute;
          top: 5px;
          right: 5px;
          background-color: white;
          border: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          opacity: 0;
          transition: all 0.2s;
          cursor: pointer;
        }
        
        .card-thumbnail:hover .remove-btn {
          opacity: 0.7;
        }
        
        .remove-btn:hover {
          opacity: 1 !important;
          background-color: var(--bs-danger);
          color: white;
        }
        
        @media (max-width: 576px) {
          .card-thumbnail {
            height: 100px;
          }
          
          .file-icon, .video-icon {
            font-size: 28px;
          }
        }
      `}</style>
    </>
  );
};

export default MediaPreview;